import React, { useState } from 'react';
import { TrendingUp, DollarSign, Award, Target, Info, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { portfolioCompanies, curqlFunds } from '../../data/centralizedData';

const InvestmentTracking = () => {
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedMetric, setSelectedMetric] = useState('all');

  // Use investment data from centralized source
  const investmentData = portfolioCompanies;
  const curqlFundsData = curqlFunds;

  // Calculate totals from all investments
  const calculateTotals = () => {
    const directTotals = investmentData.reduce((totals, investment) => ({
      commitment: totals.commitment + investment.commitment,
      invested: totals.invested + investment.invested,
      assetValue2025: totals.assetValue2025 + investment.currentValue,
      currentValue: totals.currentValue + investment.currentValue,
      efficiency: totals.efficiency + investment.returns.efficiency,
      income: totals.income + investment.returns.income,
      contract: totals.contract + investment.returns.contract,
      total: totals.total + investment.returns.total
    }), {
      commitment: 0,
      invested: 0,
      assetValue2025: 0,
      currentValue: 0,
      efficiency: 0,
      income: 0,
      contract: 0,
      total: 0
    });

    const curqlTotals = curqlFundsData.reduce((totals, fund) => ({
      commitment: totals.commitment + fund.commitment,
      invested: totals.invested + fund.invested,
      assetValue2025: totals.assetValue2025 + fund.assetValue2025,
      currentValue: totals.currentValue + fund.currentValue
    }), {
      commitment: 0,
      invested: 0,
      assetValue2025: 0,
      currentValue: 0
    });

    return {
      commitment: directTotals.commitment + curqlTotals.commitment,
      invested: directTotals.invested + curqlTotals.invested,
      assetValue2025: directTotals.assetValue2025 + curqlTotals.assetValue2025,
      currentValue: directTotals.currentValue + curqlTotals.currentValue,
      efficiency: directTotals.efficiency,
      income: directTotals.income,
      contract: directTotals.contract,
      total: directTotals.total
    };
  };

  const totalInvestments = calculateTotals();

  const formatCurrency = (value) => {
    if (value === 0) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === 0) return '0%';
    return `${(value * 100).toFixed(1)}%`;
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Onboarding': return 'bg-yellow-100 text-yellow-800';
      case 'Hold': return 'bg-gray-100 text-gray-800';
      case 'Not Active': return 'bg-red-100 text-red-800';
      case 'Pilot': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getROIColor = (roi) => {
    if (roi >= 2) return 'text-green-600 font-bold';
    if (roi >= 1) return 'text-green-600';
    if (roi > 0) return 'text-blue-600';
    return 'text-gray-500';
  };

  const toggleRowExpanded = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const filterMetrics = (investment) => {
    if (selectedMetric === 'all') return true;
    if (selectedMetric === 'efficiency' && investment.returns.efficiency > 0) return true;
    if (selectedMetric === 'income' && investment.returns.income > 0) return true;
    if (selectedMetric === 'contract' && investment.returns.contract > 0) return true;
    return false;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Investment Tracking</h2>
          <p className="text-gray-600 mt-1">Comprehensive view of all Service Ventures investments</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-xl border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <Target className="w-8 h-8 text-blue-500" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Commitment</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(totalInvestments.commitment)}</div>
          <div className="text-sm text-gray-600 mt-1">Total commitment</div>
        </div>

        <div className="bg-white p-6 rounded-xl border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-8 h-8 text-green-500" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Invested</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(totalInvestments.invested)}</div>
          <div className="text-sm text-gray-600 mt-1">Capital deployed</div>
        </div>

        <div className="bg-white p-6 rounded-xl border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-8 h-8 text-purple-500" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Current Value</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(totalInvestments.currentValue)}</div>
          <div className="text-sm text-gray-600 mt-1">
            {totalInvestments.invested > 0 ? 
              `${(totalInvestments.currentValue / totalInvestments.invested).toFixed(2)}x multiple` 
              : 'N/A'}
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <Award className="w-8 h-8 text-orange-500" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Returns</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(totalInvestments.total)}</div>
          <div className="text-sm text-gray-600 mt-1">YTD internal returns</div>
        </div>
      </div>

      {/* Three-Pillar Returns Breakdown */}
      <div className="bg-white p-6 rounded-xl border shadow-sm">
        <h3 className="text-xl font-semibold mb-6">Three-Pillar Returns Breakdown</h3>
        <div className="grid grid-cols-3 gap-6">
          <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
            <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <h5 className="text-lg font-semibold text-blue-800 mb-2">Internal Efficiency</h5>
            <div className="text-3xl font-bold text-blue-600">{formatCurrency(totalInvestments.efficiency)}</div>
            <div className="text-sm text-blue-700 mt-2">Cost savings & operational gains</div>
          </div>
          
          <div className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <DollarSign className="w-8 h-8 text-white" />
            </div>
            <h5 className="text-lg font-semibold text-green-800 mb-2">Internal Income</h5>
            <div className="text-3xl font-bold text-green-600">{formatCurrency(totalInvestments.income)}</div>
            <div className="text-sm text-green-700 mt-2">New revenue streams</div>
          </div>

          <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
            <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Award className="w-8 h-8 text-white" />
            </div>
            <h5 className="text-lg font-semibold text-purple-800 mb-2">Contract Value</h5>
            <div className="text-3xl font-bold text-purple-600">{formatCurrency(totalInvestments.contract)}</div>
            <div className="text-sm text-purple-700 mt-2">Contractual discounts & benefits</div>
          </div>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="flex items-center space-x-4">
        <span className="text-sm font-medium text-gray-700">Filter by return type:</span>
        <button
          onClick={() => setSelectedMetric('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            selectedMetric === 'all' 
              ? 'bg-gray-900 text-white' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All Investments
        </button>
        <button
          onClick={() => setSelectedMetric('efficiency')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            selectedMetric === 'efficiency' 
              ? 'bg-blue-600 text-white' 
              : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
          }`}
        >
          Efficiency Returns
        </button>
        <button
          onClick={() => setSelectedMetric('income')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            selectedMetric === 'income' 
              ? 'bg-green-600 text-white' 
              : 'bg-green-50 text-green-700 hover:bg-green-100'
          }`}
        >
          Income Returns
        </button>
        <button
          onClick={() => setSelectedMetric('contract')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            selectedMetric === 'contract' 
              ? 'bg-purple-600 text-white' 
              : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
          }`}
        >
          Contract Returns
        </button>
      </div>

      {/* Investment Table */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Investment</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Commitment</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Invested</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Current Value</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Equity ROI</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Internal ROI</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Total Returns</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {investmentData.filter(filterMetrics).map((investment) => (
                <React.Fragment key={investment.id}>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-semibold text-gray-900">{investment.name}</div>
                        <div className="text-sm text-gray-500">{investment.sector}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(investment.status)}`}>
                        {investment.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-gray-900">
                      {formatCurrency(investment.commitment)}
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-gray-900">
                      {formatCurrency(investment.invested)}
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-gray-900">
                      {formatCurrency(investment.currentValue)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className={getROIColor(investment.roi)}>
                        {investment.roi}x
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className={getROIColor(investment.internalROI)}>
                        {formatPercent(investment.internalROI)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-gray-900">
                      {formatCurrency(investment.returns.total)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => toggleRowExpanded(investment.id)}
                        className="text-blue-600 hover:text-blue-800 transition-colors"
                      >
                        {expandedRows[investment.id] ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </button>
                    </td>
                  </tr>
                  {expandedRows[investment.id] && (
                    <tr className="bg-gray-50">
                      <td colSpan="9" className="px-6 py-4">
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div>
                            <div className="font-semibold text-gray-700 mb-2">Returns Breakdown</div>
                            <div className="space-y-1">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Efficiency:</span>
                                <span className="font-medium text-blue-600">{formatCurrency(investment.returns.efficiency)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Income:</span>
                                <span className="font-medium text-green-600">{formatCurrency(investment.returns.income)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Contract:</span>
                                <span className="font-medium text-purple-600">{formatCurrency(investment.returns.contract)}</span>
                              </div>
                            </div>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-700 mb-2">Investment Details</div>
                            <div className="space-y-1">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Date:</span>
                                <span className="font-medium">{investment.investmentDate}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Equity:</span>
                                <span className="font-medium">{investment.equity}</span>
                              </div>
                            </div>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-700 mb-2">Integration Status</div>
                            <div className="space-y-1">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Progress:</span>
                                <span className="font-medium">{investment.integration}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Adoption:</span>
                                <span className="font-medium">{investment.adoption}</span>
                              </div>
                            </div>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-700 mb-2">Performance</div>
                            <div className="space-y-1">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Growth:</span>
                                <span className="font-medium">{investment.growthRate}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        {investment.monthlyHighlight && (
                          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="font-semibold text-blue-900 text-sm mb-1">Monthly Highlight</div>
                            <div className="text-sm text-blue-800">{investment.monthlyHighlight}</div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}

              {/* CURQL Funds */}
              <tr className="bg-gray-100 font-semibold">
                <td colSpan="9" className="px-6 py-3 text-sm text-gray-700 uppercase tracking-wide">
                  CURQL Fund Investments
                </td>
              </tr>
              {curqlFundsData.map((fund) => (
                <tr key={fund.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-semibold text-gray-900">{fund.name}</div>
                      <div className="text-sm text-gray-500">Venture Fund</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Active
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right font-medium text-gray-900">
                    {formatCurrency(fund.commitment)}
                  </td>
                  <td className="px-6 py-4 text-right font-medium text-gray-900">
                    {formatCurrency(fund.invested)}
                  </td>
                  <td className="px-6 py-4 text-right font-medium text-gray-900">
                    {formatCurrency(fund.currentValue)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className={getROIColor(fund.currentValue / fund.invested)}>
                      {(fund.currentValue / fund.invested).toFixed(2)}x
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-gray-500">
                    N/A
                  </td>
                  <td className="px-6 py-4 text-right text-gray-500">
                    N/A
                  </td>
                  <td className="px-6 py-4 text-center">
                    <Info className="w-5 h-5 text-gray-400 inline-block" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Export Options */}
      <div className="flex items-center justify-between p-6 bg-gray-50 rounded-xl border">
        <div>
          <h3 className="font-semibold text-gray-900">Export Data</h3>
          <p className="text-sm text-gray-600">Download investment tracking data for further analysis</p>
        </div>
        <button
          onClick={() => alert('Export functionality - requires xlsx library')}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          Export to Excel
        </button>
      </div>
    </div>
  );
};

export default InvestmentTracking;